import { localizeDateNames } from "@/lib/dateNames";
import i18n from "@/i18n";
/**
 * Workforce · shared frontend helpers.
 *
 * Kept as pure functions so tests can import them without React or
 * network. Never touch these to change enum text — the visible labels
 * are frozen by Prompt 10B.
 */

// Prompt 10B §4 · §31 — browser LOCAL calendar YYYY-MM-DD.
// MUST NOT use the UTC toISOString().slice pattern because it shifts
// the calendar day around local midnight in most timezones.
export function browserLocalYMD(d = new Date()) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${dd}`;
}

// Prompt 10B §13 — human-readable duration for restaurant-facing UI.
// Canonical backend value remains INTEGER MINUTES; this function is
// display-only. Raw minutes MUST NEVER be shown to the manager.
export function formatDurationMinutes(totalMinutes) {
    if (!Number.isFinite(totalMinutes) || totalMinutes < 0) return "";
    const total = Math.floor(totalMinutes);
    const h = Math.floor(total / 60);
    const m = total % 60;
    if (h === 0 && m === 0) return "0 h";
    if (h === 0) return `${m} min`;
    if (m === 0) return `${h} h`;
    return `${h} h ${m} min`;
}

// Prompt 10B §13 — Contract-Type label mapping for display.
export function contractTypeLabel(canonical) {
    if (canonical === "FULL_TIME") return "Full-time";
    if (canonical === "PART_TIME") return "Part-time";
    return "";
}

// Prompt 10B §13 — Compact "Contract" column value.
export function formatContractCell({ contract_type, contract_hours_minutes }) {
    const type = contractTypeLabel(contract_type);
    const duration = formatDurationMinutes(contract_hours_minutes);
    return `${type} · ${duration} / 3 weeks`;
}

// ─── Prompt 10C · pure display helpers ───────────────────────

// Prompt 10C §19 · §23 — DD.MM.YYYY, no locale automation.
// Returns "—" for null/empty/invalid input so callers never expose
// null / undefined / "Invalid Date" text to the manager.
export function formatYMDDate(ymd) {
    if (!ymd || typeof ymd !== "string") return "—";
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
    if (!match) return "—";
    const [, y, m, d] = match;
    return `${d}.${m}.${y}`;
}

// Prompt 10C §16 · §26.
export function workingTimeModelLabel(canonical) {
    if (canonical === "STANDARD") return i18n.t("wf.workforceHelpers.standard", "Standard");
    if (canonical === "AVERAGING") return "Tasoittumisjärjestelmä";
    return "";
}

// Prompt 10C §17 · §26.
export function tesFamilyLabel(canonical) {
    if (canonical === "PAM_MARA") return i18n.t("wf.workforceHelpers.hospitalityIndustryPamMara", "Hospitality industry (PAM–MaRa)");
    return "";
}

// Prompt 10C §18 · §26.
export function payrollIncludedLabel(value) {
    if (value === true) return i18n.t("common.yes", "Yes");
    if (value === false) return "No";
    return "—";
}

// Prompt 10C §10.
export function employeeStatusLabel(canonical) {
    if (canonical === "ACTIVE") return i18n.t("common.active", "Active");
    if (canonical === "INACTIVE") return i18n.t("common.inactive", "Inactive");
    return canonical || "—";
}

// Prompt 10C §20.
export function employeeMobileStatusLabel(canonical) {
    if (canonical === "NOT_ACTIVATED") return i18n.t("wf.workforceHelpers.notActivated", "Not activated");
    if (canonical === "ACTIVATED") return i18n.t("wf.workforceHelpers.activated", "Activated");
    return "—";
}

// ─── Prompt 12E · pure display helpers ───────────────────────
// Prompt 12E §16 — approved English weekday/month labels. Frozen
// intentionally to avoid locale-dependent output for managers.
const _WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const _MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
                    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// Prompt 12E §16 — returns { weekday, day, month } for one canonical
// YYYY-MM-DD Schedule date. Callers own the visual layout.
export function formatScheduleDateHeader(ymd) {
    if (!ymd || typeof ymd !== "string") return null;
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
    if (!m) return null;
    const [, y, mm, dd] = m;
    // JavaScript Date's day-of-week from a UTC-midnight ISO date is
    // stable everywhere: the same wall-clock day maps to the same
    // weekday. That is exactly what we want for a display grid.
    const d = new Date(`${y}-${mm}-${dd}T00:00:00Z`);
    return {
        weekday: localizeDateNames(_WEEKDAYS[d.getUTCDay()]),
        day: String(parseInt(dd, 10)),
        month: localizeDateNames(_MONTHS[parseInt(mm, 10) - 1], { lower: true }),
    };
}

// Prompt 12E §11 — Period range "DD.MM.YYYY – DD.MM.YYYY".
export function formatPeriodRange(startYmd, endYmd) {
    const s = formatYMDDate(startYmd);
    const e = formatYMDDate(endYmd);
    return `${s} – ${e}`;
}

// ─── Prompt 12G-1 · Quick Shift Input Parser ─────────────────
// Pure input → output helper. Converts one manager-entered Shift
// shorthand (e.g. "8-16", "8:30-16:30", "18-02") into the exact
// date/time payload later consumed by Prompt-12D Shift APIs.
//
// Prompt 12G-1 §22 — pure helper: no API calls, no DOM, no user
// notifications, no React state, no storage, no external mutation.
// Prompt 12G-1 §20 · §21 — no duration output, no legal / rest /
// pay / meal calculation. Structural + calendar math only.

// Prompt 12G-1 §10 · §11 — strict interval grammar. Single hyphen,
// no internal whitespace, exactly one start clock and one end clock.
// Hour token: 1 or 2 digits. Optional ":MM" where MM is exactly 2
// digits. Reject "8:", "8:0", "008", "008:00", ":30", "8:000".
const _QUICK_SHIFT_RE = /^(\d{1,2})(?::(\d{2}))?-(\d{1,2})(?::(\d{2}))?$/;

// Prompt 12G-1 §18 — strict canonical YYYY-MM-DD, zero-padded parts.
const _WORKDATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

// Prompt 12G-1 §13 — 10-minute resolution. Explicit minutes are valid
// only when they belong to this closed set. Omitted minutes normalize
// to "00" (Prompt 12G-1 §13 · §14).
const _ALLOWED_MINUTES = new Set(["00", "10", "20", "30", "40", "50"]);

function _isLeapYear(y) {
    return (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;
}

function _daysInMonth(y, m) {
    if (m === 1 || m === 3 || m === 5 || m === 7 ||
        m === 8 || m === 10 || m === 12) return 31;
    if (m === 4 || m === 6 || m === 9 || m === 11) return 30;
    if (m === 2) return _isLeapYear(y) ? 29 : 28;
    return 0;
}

// Prompt 12G-1 §18 — return decomposed integer parts only when the
// string is a real Gregorian calendar date. Reject anything else.
function _parseWorkDate(s) {
    if (typeof s !== "string") return null;
    const match = _WORKDATE_RE.exec(s);
    if (!match) return null;
    const y = parseInt(match[1], 10);
    const mo = parseInt(match[2], 10);
    const d = parseInt(match[3], 10);
    if (mo < 1 || mo > 12) return null;
    const dim = _daysInMonth(y, mo);
    if (d < 1 || d > dim) return null;
    return { y, mo, d };
}

// Prompt 12G-1 §19 — increment a validated {y, mo, d} by exactly one
// calendar day. Pure integer math, no Date object, no timezone.
function _addOneDay({ y, mo, d }) {
    let ny = y;
    let nm = mo;
    let nd = d + 1;
    if (nd > _daysInMonth(ny, nm)) {
        nd = 1;
        nm += 1;
        if (nm > 12) {
            nm = 1;
            ny += 1;
        }
    }
    const yy = String(ny).padStart(4, "0");
    const mm = String(nm).padStart(2, "0");
    const dd = String(nd).padStart(2, "0");
    return `${yy}-${mm}-${dd}`;
}

// Prompt 18B2-P4 §6 · §7 · §8 · §9 — friendly Workforce manager time
// input normalization. Pure, no DOM, no network, no React state.
//
// Accepts one time token in the manager's typing habit:
//   "20:20"  → "20:20"
//   "20.20"  → "20:20"
//   "2020"   → "20:20"
//   "9:00"   → "09:00"
//   "9.00"   → "09:00"
//   "900"    → "09:00"
//   "0900"   → "09:00"
//   "9"      → "09:00"   (bare hour token, zero-padded)
//
// Rejects any clock-invalid value:
//   "25:00"  → null      (hour out of range)
//   "2365"   → null      (minute out of range)
//   "20.75"  → null      (minute out of range)
//   "abcd"   → null      (non-numeric)
//
// This is INPUT SYNTAX ONLY (§9). It does NOT introduce a
// 10-minute restriction; existing per-field business validation
// (e.g. Employee Clock reported-time 10-minute alignment,
// planned-shift `_ALLOWED_MINUTES` in `parseQuickShiftInput`)
// continues to run downstream.
export function normalizeTimeInput(raw) {
    if (typeof raw !== "string") return null;
    const t = raw.trim();
    if (t === "") return null;
    let hh; let mm;
    if (/^\d{1,2}$/.test(t)) {
        // Bare hour token like "9" / "09" → HH:00.
        hh = t.padStart(2, "0");
        mm = "00";
    } else if (/^\d{1,2}:\d{2}$/.test(t)) {
        const [h, m] = t.split(":");
        hh = h.padStart(2, "0");
        mm = m;
    } else if (/^\d{1,2}\.\d{2}$/.test(t)) {
        const [h, m] = t.split(".");
        hh = h.padStart(2, "0");
        mm = m;
    } else if (/^\d{3,4}$/.test(t)) {
        // Compact digits: "900" → "9:00", "0900" → "09:00",
        // "2020" → "20:20".
        mm = t.slice(-2);
        hh = t.slice(0, -2).padStart(2, "0");
    } else {
        return null;
    }
    const H = parseInt(hh, 10);
    const M = parseInt(mm, 10);
    if (!Number.isInteger(H) || !Number.isInteger(M)) return null;
    if (H < 0 || H > 23) return null;
    if (M < 0 || M > 59) return null;
    return `${hh}:${mm}`;
}

// Prompt 18B2-P4 §10 · §11 — pre-normalize a two-token interval like
// "20.20-22.30" or "2020-2230" so the existing strict Shift interval
// parser (`parseQuickShiftInput`) can consume it verbatim. Bare hour
// tokens ("8-16") are preserved. Returns `null` if the shape is not
// exactly ONE hyphen with two normalizable tokens; callers may then
// fall back to the raw string so `parseQuickShiftInput` decides.
export function normalizeShiftIntervalInput(raw) {
    if (typeof raw !== "string") return null;
    const s = raw.trim();
    if (s === "") return null;
    const parts = s.split("-");
    if (parts.length !== 2) return null;
    const [lhs, rhs] = parts;
    const _normToken = (tok) => {
        const t = (tok || "").trim();
        if (t === "") return null;
        if (/^\d{1,2}$/.test(t)) return t;           // bare hour
        if (/^\d{1,2}:\d{2}$/.test(t)) return t;      // canonical
        if (/^\d{1,2}\.\d{2}$/.test(t)) {              // dot → colon
            return t.replace(".", ":");
        }
        if (/^\d{3,4}$/.test(t)) {                    // compact → HH:MM
            return `${t.slice(0, -2)}:${t.slice(-2)}`;
        }
        return null;
    };
    const left = _normToken(lhs);
    const right = _normToken(rhs);
    if (left === null || right === null) return null;
    return `${left}-${right}`;
}

export function parseQuickShiftInput(value, workDate) {
    // Prompt 12G-1 §7 — normal invalid input returns null; no throw.
    if (typeof value !== "string") return null;

    // Prompt 12G-1 §8 · §9 — trim only leading/trailing whitespace of
    // the whole string. Internal hyphen whitespace stays rejected.
    const trimmed = value.trim();
    const match = _QUICK_SHIFT_RE.exec(trimmed);
    if (!match) return null;

    const [, h1s, m1s, h2s, m2s] = match;

    // Prompt 12G-1 §13 — 10-minute resolution when minutes are
    // explicit. When omitted the effective value is "00" which is
    // allowed by definition.
    if (m1s !== undefined && !_ALLOWED_MINUTES.has(m1s)) return null;
    if (m2s !== undefined && !_ALLOWED_MINUTES.has(m2s)) return null;

    // Prompt 12G-1 §12 — hour 0..23 inclusive.
    const h1 = parseInt(h1s, 10);
    const h2 = parseInt(h2s, 10);
    if (h1 < 0 || h1 > 23) return null;
    if (h2 < 0 || h2 > 23) return null;

    const m1 = m1s === undefined ? 0 : parseInt(m1s, 10);
    const m2 = m2s === undefined ? 0 : parseInt(m2s, 10);

    // Prompt 12G-1 §14 — canonical HH:MM output.
    const start_time =
        `${String(h1).padStart(2, "0")}:${String(m1).padStart(2, "0")}`;
    const end_time =
        `${String(h2).padStart(2, "0")}:${String(m2).padStart(2, "0")}`;

    // Prompt 12G-1 §17 — equal start/end is invalid quick input.
    if (start_time === end_time) return null;

    // Prompt 12G-1 §18 — strict workDate validation.
    const parsedDate = _parseWorkDate(workDate);
    if (!parsedDate) return null;

    // Prompt 12G-1 §15 · §16 — same-day vs overnight branch.
    const startMinutes = h1 * 60 + m1;
    const endMinutes = h2 * 60 + m2;

    let end_date;
    if (endMinutes > startMinutes) {
        end_date = workDate;
    } else {
        // endMinutes < startMinutes (equal case already returned null).
        end_date = _addOneDay(parsedDate);
    }

    // Prompt 12G-1 §6 — exact return shape only.
    return { start_time, end_date, end_time };
}

// ─── TES-B2C1B1 · VV / Vuosivapaa display helpers ────────────
export const VV_SYSTEM_OPTIONS = [
    { value: "STANDARD_VV", get label() { return i18n.t("wf.workforceHelpers.VV_SYSTEM_OPTIONS.label_standardVv", "Standard VV"); } },
    { value: "REGULAR_NIGHT_VV", get label() { return i18n.t("wf.workforceHelpers.VV_SYSTEM_OPTIONS.label_regularNightWorkVv", "Regular night-work VV"); } },
    { value: "THREE_SHIFT_NO_VV", get label() { return i18n.t("wf.workforceHelpers.VV_SYSTEM_OPTIONS.label_threeShiftNoStandardVv", "Three-shift / no standard VV"); } },
    { value: "OFFICE_PROTOCOL", get label() { return i18n.t("wf.workforceHelpers.VV_SYSTEM_OPTIONS.label_officeWorkTimeProtocol", "Office work-time protocol"); } },
    { value: "PERSONNEL_RESTAURANT_LOCAL_ALTERNATIVE",
        get label() { return i18n.t("wf.workforceHelpers.VV_SYSTEM_OPTIONS.label_personnelRestaurantLocalArrangement", "Personnel restaurant local arrangement"); } },
];

export const VV_UNSUPPORTED_SYSTEMS = new Set([
    "REGULAR_NIGHT_VV", "OFFICE_PROTOCOL",
    "PERSONNEL_RESTAURANT_LOCAL_ALTERNATIVE",
]);

export const VV_MANAGE_ROLES = new Set([
    "restaurant", "restaurant_admin", "corporate",
]);

export function vvSystemLabel(canonical) {
    const opt = VV_SYSTEM_OPTIONS.find((o) => o.value === canonical);
    return opt ? opt.label : "Not set";
}

// Display-only hours + minutes → integer minutes (no floats).
export function hoursMinutesToMinutes(hours, minutes) {
    const h = Number.isInteger(hours) && hours >= 0 ? hours : 0;
    const m = Number.isInteger(minutes) && minutes >= 0 ? minutes : 0;
    return h * 60 + m;
}

export const VL_BASIS_FIRST_OF_MONTH_MSG =
    "This version supports earning-basis changes from the first day of a month.";

// Release limitation (not a legal rule): basis changes only from the 1st of a month.
export function basisChangeNotFirstOfMonth(currentBasis, basis, effectiveFrom) {
    return Boolean(currentBasis) && Boolean(basis) && basis !== currentBasis
        && Boolean(effectiveFrom) && !effectiveFrom.endsWith("-01");
}

export function termsRevisionErrorMessage(err) {
    const code = err?.response?.data?.detail?.code;
    switch (code) {
    case "TERMS_REVISION_MID_PERIOD_UNSUPPORTED":
        return i18n.t("wf.workforceHelpers.employmentTermsChangesMustStartAt", "Employment Terms changes must start at the beginning of a Workforce period.");
    case "TERMS_REVISION_CLOSED_PERIOD":
        return i18n.t("wf.workforceHelpers.thisChangeWouldAffectAClosed", "This change would affect a closed period. Reopen the period first or choose a later period start.");
    case "VUOSILOMA_BASIS_CHANGE_FIRST_OF_MONTH_ONLY":
        return i18n.t("wf.workforceHelpers.VL_BASIS_FIRST_OF_MONTH_MSG", VL_BASIS_FIRST_OF_MONTH_MSG);
    case "TERMS_REVISION_SAME_DATE":
        return i18n.t("wf.workforceHelpers.aTermsChangeAlreadyStartsOn", "A Terms change already starts on this date. Choose another date.");
    case "TERMS_REVISION_NO_APPLICABLE_PREDECESSOR":
        return i18n.t("wf.workforceHelpers.noEmploymentTermsAreInEffect", "No Employment Terms are in effect on this date.");
    case "TERMS_TIMELINE_AMBIGUOUS":
        return i18n.t("wf.workforceHelpers.theEmploymentTermsHistoryNeedsReview", "The Employment Terms history needs review before it can be changed.");
    case "TRANSFER_SAME_MAIN_RESTAURANT":
        return i18n.t("wf.workforceHelpers.theEmployeeAlreadyWorksMainlyAt", "The employee already works mainly at this restaurant.");
    case "TRANSFER_DESTINATION_NOT_IN_COMPANY":
        return i18n.t("wf.workforceHelpers.chooseARestaurantOfYourCompany", "Choose a restaurant of your company.");
    case "TRANSFER_DESTINATION_CLOSED_PERIOD":
        return i18n.t("wf.workforceHelpers.theNewRestaurantHasAClosed", "The new restaurant has a closed period on or after this date. Choose a later date.");
    case "TRANSFER_SOURCE_NOT_IN_SCOPE":
        return i18n.t("wf.workforceHelpers.openTheEmployeeFromTheirCurrent", "Open the employee from their current main restaurant.");
    default:
        if (err?.response?.status === 422) {
            return i18n.t("wf.workforceHelpers.checkTheDateAndVvSystem", "Check the date and VV system and try again.");
        }
        return i18n.t("wf.workforceHelpers.couldNotSaveTheChangeTry", "Could not save the change. Try again.");
    }
}
